/**
 * appAccess.js (OPUS-901) — soft app-entry gate for Aether.
 *
 * Reads the access token's permissions[] (minted by user-management) and checks
 * apps.aether.access. SOFT by default: it only logs a would-deny so existing
 * users are never locked out while the role->permission map is finalised. Flip
 * ENFORCE to true (or wire to an env flag) to hard-block via <AccessDenied>.
 */
const APP_PERMISSION = "apps.aether.access";
const QUICKLINK_PERMISSION = "quicklinks.aether.view";
export const ENFORCE_APP_ACCESS = false; // soft: log only, never block

const decodePermissions = (token) => {
  try {
    if (!token || typeof token !== "string") return [];
    const part = token.split(".")[1];
    if (!part) return [];
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return Array.isArray(payload.permissions) ? payload.permissions : [];
  } catch (_) {
    return [];
  }
};

/**
 * Returns the decoded permissions[] for a session (from its access token).
 * Reuses the same token-resolution the app-access gate uses.
 */
export const getSessionPermissions = (session) => {
  const token = session?.accessKey || session?.accessToken || null;
  return decodePermissions(token);
};

/**
 * Super-admin check — the access token carries the wildcard '*' permission.
 * Used to gate the Custom Stats screen (route + nav item).
 */
export const isSuperAdmin = (session) => getSessionPermissions(session).includes("*");

/**
 * Returns true if the session may access Aether. super_admin ('*') always passes.
 * In soft mode the caller should treat a false result as advisory (log only).
 */
export const hasAetherAccess = (session) => {
  const token = session?.accessKey || session?.accessToken || null;
  const perms = decodePermissions(token);
  const allowed =
    perms.includes("*") ||
    perms.includes(APP_PERMISSION) ||
    perms.includes(QUICKLINK_PERMISSION);
  if (!allowed) {
    console.warn(
      `[RBAC shadow] would-deny Aether entry: missing ${APP_PERMISSION} (token perms: ${perms.length})`
    );
  }
  return allowed;
};
